import ExpandCircleDownOutlinedIcon from "@mui/icons-material/ExpandCircleDownOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Alert,
  Box,
  Breadcrumbs,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import * as React from "react";
import { Link } from "react-router-dom";

import { useAuth, useNavigation } from "@/providers";
import { PageLayout, RobotoButton } from "@/shared/components";
import { UISimpleCondition } from "@/shared/components/filters";
import {
  ActionParameter,
  ActionRecord,
  InvocationInput,
  InvocationInputs,
  InvocationUploadDestination,
  UploadDestinationType,
} from "@/shared/domain/actions";
import {
  useAction,
  useCreateEventTrigger,
  useCreateScheduledTrigger,
} from "@/shared/domain/actions/hooks";
import {
  TriggerEvaluationCause,
  TriggerForEachPrimitive,
  TriggerType,
  BaseCreateTriggerRequest,
  CreateEventTriggerRequest,
  CreateScheduledTriggerRequest,
} from "@/shared/domain/actions/TriggerRecord";
import { OrganizationTier } from "@/shared/domain/orgs";
import { QueryTarget } from "@/shared/domain/query";
import { ConditionGroup, ConditionsOperator } from "@/types";

import {
  ActionComputeReqs,
  ActionContainerParams,
  ActionInputData,
  ActionParamsTable,
  ActionSelector,
  ActionTimeoutField,
  DatasetSelector,
} from "../components/action";
import {
  actionNameError,
  createInitialTimeoutState,
  isActionNameValid,
  isActionTimeoutValid,
  parseComputeRequirements,
  parseContainerParameters,
  parseTimeout,
} from "../components/action/actionUtils";
import {
  TriggerCauseSelector,
  TriggerCondition,
  TriggerForEachSelector,
  TriggerInvocationInput,
  TriggerSchedule,
} from "../components/trigger";
import { extractResourcePrefix } from "../components/trigger/TriggerConditionForm/utils";

function triggerFormHasErrors(props: {
  name: string | undefined;
  triggerType: TriggerType;
  action: ActionRecord | null;
  actionTimeout: number | string | null | undefined;
  requiredInputs: string[] | undefined;
  hasComputeRequirementsErrors: boolean;
  hasScheduleErrors: boolean;
}): boolean {
  const { name, action, actionTimeout, hasComputeRequirementsErrors } = props;

  // Name checks are applicable to all trigger types.
  if (name === undefined || name.length === 0 || actionNameError(name)) {
    return true;
  }

  // Action checks are applicable to all trigger types.
  if (
    action === null ||
    !isActionTimeoutValid(actionTimeout) ||
    hasComputeRequirementsErrors
  ) {
    return true;
  }

  // Trigger-type-specific correctness checks.
  const { triggerType, requiredInputs, hasScheduleErrors } = props;
  if (triggerType === TriggerType.EventDriven && requiredInputs === undefined) {
    return true;
  }

  if (triggerType === TriggerType.Scheduled && hasScheduleErrors) {
    return true;
  }

  return false; // no form errors
}

export function CreateTrigger() {
  const theme = useTheme();

  const { currentOrganization } = useAuth();
  const isGated =
    currentOrganization?.tier === OrganizationTier.Free ? true : false;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errorText, setErrorText] = React.useState<string>("");

  // Fields common to both types of triggers.
  const [name, setName] = React.useState<string | undefined>("");
  const [triggerType, setTriggerType] = React.useState<TriggerType>(
    TriggerType.EventDriven,
  );
  const [action, setAction] = React.useState<ActionRecord | null>(null);

  const [cpu, setCpu] = React.useState<string | undefined>(undefined);
  const [memory, setMemory] = React.useState<string | undefined>(undefined);
  const [gpu, setGpu] = React.useState<string | undefined>(undefined);
  const [storage, setStorage] = React.useState<number | undefined>(undefined);
  const [workdir, setWorkdir] = React.useState<string | undefined>(undefined);
  const [command, setCommand] = React.useState<string | undefined>(undefined);
  const [entrypoint, setEntrypoint] = React.useState<string | undefined>(
    undefined,
  );

  const [envVars, setEnvVars] = React.useState<
    Record<string, string> | undefined
  >(undefined);

  const [parameters, setParameters] = React.useState<
    ActionParameter[] | undefined
  >(undefined);

  const [parameterValues, setParameterValues] = React.useState<
    | {
        [key: string]: unknown;
      }
    | undefined
  >(undefined);

  const [actionTimeout, setActionTimeout] = React.useState<
    number | string | null | undefined
  >(createInitialTimeoutState(isGated));

  const [hasComputeFormErrors, setHasComputeFormErrors] = React.useState(false);

  // Fields specific to event-driven triggers.
  const [requiredInputs, setRequiredInputs] = React.useState<
    string[] | undefined
  >(undefined);
  const [additionalInputs, setAdditionalInputs] = React.useState<
    string[] | undefined
  >(undefined);
  const [causes, setCauses] = React.useState<TriggerEvaluationCause[]>([
    TriggerEvaluationCause.FileUpload,
  ]);

  const [operator, setOperator] = React.useState<ConditionsOperator>("AND");
  const [conditions, setConditions] = React.useState<UISimpleCondition[]>([]);
  const [forEachVal, setForEachVal] = React.useState(
    TriggerForEachPrimitive.DatasetFile,
  );

  // Fields specific to scheduled triggers.
  const [schedule, setSchedule] = React.useState("0 0 * * *"); // Midnight UTC
  const [scheduleError, setScheduleError] = React.useState<string | undefined>(
    undefined,
  );
  const [roboqlQuery, setRoboqlQuery] = React.useState<string | undefined>(
    undefined,
  );
  const [queryTarget, setQueryTarget] = React.useState<QueryTarget>(
    QueryTarget.Files,
  );
  const [outputDatasetId, setOutputDatasetId] = React.useState<string | null>(
    null,
  );

  // Collapse controls for optional trigger settings.
  const [openAdditionalInputs, setOpenAdditionalInputs] =
    React.useState<boolean>(false);

  const [openForEachVal, setOpenForEachVal] = React.useState<boolean>(false);
  const [openContainerParams, setOpenContainerParams] =
    React.useState<boolean>(false);
  const [openResourceParams, setOpenResourceParams] =
    React.useState<boolean>(false);
  const [openOutputDataset, setOpenOutputDataset] = React.useState(false);

  const createEventTrigger = useCreateEventTrigger();
  const createScheduledTrigger = useCreateScheduledTrigger();

  const { goto } = useNavigation();

  const createTrigger = () => {
    setLoading(true);

    // Validate file-scoped conditions for event-driven triggers
    if (triggerType === TriggerType.EventDriven) {
      const hasFileScopedConditions = conditions.some((condition) => {
        const prefix = extractResourcePrefix(condition.field);
        return prefix === "file";
      });

      if (
        hasFileScopedConditions &&
        forEachVal === TriggerForEachPrimitive.Dataset
      ) {
        setErrorText(
          "File-scoped conditions cannot be used with dataset-scoped triggers. Either change the trigger to run per-file or update the conditions to use dataset properties.",
        );
        setLoading(false);
        return;
      }
    }

    const computeRequirements = parseComputeRequirements(
      cpu,
      memory,
      gpu,
      storage,
    );

    const containerParameters = parseContainerParameters(
      command,
      entrypoint,
      workdir,
      envVars,
    );

    // Ugh. We check required parameters for nullity in the form
    // error handler, but this code doesn't know we've already
    // ensured they're good.
    const baseCreateRequest: BaseCreateTriggerRequest = {
      name: name ?? "",
      action_name: action?.name ?? "",
      action_owner_id: action?.org_id ?? "",
      enabled: true,
      compute_requirement_overrides: computeRequirements,
      container_parameter_overrides: containerParameters,
      parameter_values: parameterValues ?? null,
      timeout: actionTimeout ? parseTimeout(actionTimeout) : null,
    };

    if (triggerType === TriggerType.EventDriven) {
      let group: ConditionGroup | null;
      if (conditions.length === 0) {
        group = null;
      } else {
        group = {
          conditions: conditions,
          operator: operator,
        };
      }

      const createRequest: CreateEventTriggerRequest = {
        ...baseCreateRequest,
        for_each: forEachVal,
        required_inputs: requiredInputs ?? [],
        additional_inputs: additionalInputs ?? null,
        condition: group,
        causes: causes,
        action_digest: null,
        service_user_id: null,
      };

      createEventTrigger.mutate(createRequest, {
        onError: (error) => {
          setErrorText(error.message);
        },
        onSuccess: (triggerView) => {
          goto.trigger({
            triggerId: triggerView.name,
          });
        },
        onSettled: () => {
          setLoading(false);
        },
      });
    } else {
      let invocationInput: InvocationInput | null = null;
      if (roboqlQuery !== undefined && roboqlQuery.trim().length > 0) {
        const trimmedQuery = roboqlQuery.trim();

        switch (queryTarget) {
          case QueryTarget.Files:
            invocationInput = InvocationInputs.fileQuery(trimmedQuery);
            break;

          case QueryTarget.Topics:
            invocationInput = InvocationInputs.topicQuery(trimmedQuery);
            break;
        }
      }

      let uploadDestination: InvocationUploadDestination | null = null;
      if (outputDatasetId !== null) {
        uploadDestination = {
          destination_id: outputDatasetId,
          destination_type: UploadDestinationType.Dataset,
        };
      }

      const createRequest: CreateScheduledTriggerRequest = {
        ...baseCreateRequest,
        schedule: schedule,
        invocation_input: invocationInput,
        invocation_upload_destination: uploadDestination,
      };

      createScheduledTrigger.mutate(createRequest, {
        onError: (error) => {
          setErrorText(error.message);
        },
        onSuccess: (triggerView) => {
          goto.trigger({
            triggerId: triggerView.name,
          });
        },
        onSettled: () => {
          setLoading(false);
        },
      });
    }
  };

  const actionQuery = useAction(action?.name, action?.digest, {
    resourceOwnerId: action?.org_id || currentOrganization?.org_id,
  });

  React.useEffect(() => {
    if (actionQuery.data) {
      setParameters(actionQuery.data?.parameters);
      setActionTimeout(actionQuery.data?.timeout);
    }
  }, [actionQuery.data]);

  return (
    <PageLayout
      title={"Roboto - Create Trigger"}
      headerContentLeft={
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          sx={{
            fontSize: "0.95rem",
            fontWeight: "500",
            borderBottom: "unset",
            "& a": {
              color: theme.palette.text.secondary,
              textDecoration: "none",
            },
            "& a:hover": {
              textDecoration: "underline",
            },
          }}
        >
          <Link to="/triggers">Triggers</Link>,
          <Typography
            sx={{ fontSize: "0.95rem", fontWeight: "500" }}
            color="text.primary"
          >
            Create Trigger
          </Typography>
        </Breadcrumbs>
      }
    >
      <Box>
        <Box
          sx={{
            mb: theme.spacing(2),
          }}
        >
          {errorText ? (
            <Alert severity="error">{errorText}</Alert>
          ) : (
            <Alert severity="info">
              Complete the form below to create a new trigger.
            </Alert>
          )}
        </Box>
        <Box
          component={Paper}
          variant="outlined"
          sx={{ p: theme.spacing(2), mt: theme.spacing(1), background: "none" }}
        >
          <Stack
            sx={{
              maxWidth: "75ch",
            }}
            spacing={3}
          >
            <Box>
              <Box sx={{ mb: theme.spacing(1) }}>
                <Typography variant="subtitle2">Trigger Name</Typography>
                <Typography variant="caption">
                  Give your trigger a unique name. Note, this can&apos;t be
                  modified after.
                </Typography>
              </Box>
              <TextField
                id="outlined-trigger-name"
                placeholder="Enter a name"
                size="small"
                sx={{ width: 400 }}
                value={name}
                error={actionNameError(name)}
                helperText={
                  name === undefined || (name && isActionNameValid(name))
                    ? ""
                    : "Names cannot contain spaces or special characters"
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setName(event.target.value);
                }}
              />
            </Box>

            <Box>
              <Box sx={{ mb: theme.spacing(1) }}>
                <Typography variant="subtitle2">Trigger Type</Typography>
                <Typography variant="caption">
                  Choose whether this trigger runs on a schedule or in response
                  to events.
                </Typography>
              </Box>
              <ToggleButtonGroup
                color="primary"
                exclusive
                onChange={(_event, value: TriggerType) => {
                  if (value) {
                    setTriggerType(value);
                  }
                }}
                size="small"
                sx={{ mb: theme.spacing(0.5), height: 30 }}
                value={triggerType}
              >
                <ToggleButton
                  value={TriggerType.EventDriven}
                  sx={{ textTransform: "none" }}
                >
                  Event-Driven
                </ToggleButton>
                <ToggleButton
                  value={TriggerType.Scheduled}
                  sx={{ textTransform: "none" }}
                >
                  Scheduled
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {triggerType === TriggerType.EventDriven && (
              <Box>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="subtitle2">Trigger On</Typography>
                  <Typography variant="caption">
                    Select the events that will cause this trigger to evaluate.
                  </Typography>
                </Box>
                <TriggerCauseSelector causes={causes} setCauses={setCauses} />
              </Box>
            )}

            {triggerType === TriggerType.Scheduled && (
              <Box>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="subtitle2">Schedule (UTC)</Typography>
                  <Typography variant="caption">
                    Enter a cron expression to define when this trigger should
                    run.
                  </Typography>
                </Box>
                <TriggerSchedule
                  editable
                  schedule={schedule}
                  onScheduleChanged={setSchedule}
                  scheduleError={scheduleError}
                  onScheduleError={setScheduleError}
                />
              </Box>
            )}

            <Box>
              <Box sx={{ mb: theme.spacing(1) }}>
                <Typography variant="subtitle2">Action</Typography>
                <Typography variant="caption">
                  Select the action this trigger will invoke from your org or
                  the Action Hub.
                </Typography>
              </Box>
              <ActionSelector
                action={action}
                setAction={setAction}
                currentOrg={currentOrganization}
              />
            </Box>

            {parameters && parameters.length > 0 && (
              <Box>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="subtitle2">Parameters</Typography>
                  <Typography variant="caption">
                    Provide values for this action&apos;s parameters.
                  </Typography>
                </Box>
                <ActionParamsTable
                  editable={true}
                  mode={"values"}
                  params={parameters}
                  setParams={setParameters}
                  paramValues={parameterValues}
                  setParamValues={setParameterValues}
                />
              </Box>
            )}

            {triggerType === TriggerType.EventDriven && (
              <Box>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="subtitle2">Required Inputs</Typography>
                  <Typography variant="caption">
                    File patterns of data that must be present in a dataset for
                    this trigger to fire.
                    <br />
                    Example:<strong> **/cam_front/*.jpg</strong>
                  </Typography>
                </Box>
                <ActionInputData
                  inputData={requiredInputs || [""]}
                  setInputData={setRequiredInputs}
                  minRequired={1}
                />
              </Box>
            )}

            {triggerType === TriggerType.Scheduled && (
              <Box>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="subtitle2">
                    Invocation Input (optional)
                  </Typography>
                  <Typography variant="caption">
                    Use RoboQL to specify what data each scheduled invocation
                    should process.
                  </Typography>
                </Box>
                <TriggerInvocationInput
                  roboqlQuery={roboqlQuery}
                  onRoboqlQueryChanged={(newQuery: string | undefined) => {
                    setRoboqlQuery(newQuery);
                  }}
                  queryTarget={queryTarget}
                  onQueryTargetChanged={(newTarget: QueryTarget) => {
                    setRoboqlQuery(undefined);
                    setQueryTarget(newTarget);
                  }}
                />
              </Box>
            )}

            {triggerType === TriggerType.EventDriven && (
              <Box>
                <Box sx={{ mb: theme.spacing(1) }}>
                  <Typography variant="subtitle2">
                    Conditions (optional)
                  </Typography>
                  <Typography variant="caption">
                    Rules that determine whether the trigger should run. <br />
                    Example: only trigger this action if dataset tags contains:{" "}
                    <i>sunny</i>
                  </Typography>
                </Box>
                <TriggerCondition
                  conditions={conditions}
                  setConditions={setConditions}
                  operator={operator}
                  setOperator={setOperator}
                  editable={true}
                  forEachPrimitive={forEachVal}
                />
              </Box>
            )}

            <ActionTimeoutField
              isGated={isGated}
              showAlert={true}
              actionTimeout={actionTimeout}
              setActionTimeout={setActionTimeout}
            />

            {triggerType === TriggerType.EventDriven && (
              <>
                <Box>
                  <Box sx={{ mb: theme.spacing(1.5) }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle2">
                        Additional Inputs (optional)
                      </Typography>
                      <IconButton
                        aria-label="additional-inputs-open"
                        size="small"
                        sx={{
                          ml: theme.spacing(0.25),
                          transform: openAdditionalInputs
                            ? "rotate(180deg)"
                            : "none",
                        }}
                        onClick={() =>
                          setOpenAdditionalInputs(!openAdditionalInputs)
                        }
                      >
                        <ExpandCircleDownOutlinedIcon
                          color="primary"
                          fontSize="small"
                        />
                      </IconButton>
                    </Box>
                    <Typography variant="caption">
                      File patterns of additional data that will also be mounted
                      if available.
                    </Typography>
                  </Box>

                  <Collapse
                    in={openAdditionalInputs}
                    timeout="auto"
                    unmountOnExit
                  >
                    <ActionInputData
                      inputData={additionalInputs || [""]}
                      setInputData={setAdditionalInputs}
                      minRequired={0}
                    />
                  </Collapse>
                </Box>

                <Box>
                  <Box sx={{ mb: theme.spacing(1.5) }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Typography variant="subtitle2">
                        Invoke For Each (optional)
                      </Typography>
                      <IconButton
                        aria-label="invoke-for-each"
                        size="small"
                        sx={{
                          ml: theme.spacing(0.25),
                          transform: openForEachVal ? "rotate(180deg)" : "none",
                        }}
                        onClick={() => setOpenForEachVal(!openForEachVal)}
                      >
                        <ExpandCircleDownOutlinedIcon
                          color="primary"
                          fontSize="small"
                        />
                      </IconButton>
                    </Box>
                    <Typography variant="caption">
                      A trigger can invoke an action once for each matching
                      dataset, or once for each matching file.
                    </Typography>
                  </Box>
                  <Collapse in={openForEachVal} timeout="auto" unmountOnExit>
                    <TriggerForEachSelector
                      forEach={forEachVal}
                      setForEach={setForEachVal}
                    />
                  </Collapse>
                </Box>
              </>
            )}

            <Box>
              <Box sx={{ mb: theme.spacing(2) }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="subtitle2">
                    Container Parameter Overrides (optional)
                  </Typography>
                  <IconButton
                    aria-label="open-container-params"
                    size="small"
                    sx={{
                      ml: theme.spacing(0.25),
                      transform: openContainerParams
                        ? "rotate(180deg)"
                        : "none",
                    }}
                    onClick={() => setOpenContainerParams(!openContainerParams)}
                  >
                    <ExpandCircleDownOutlinedIcon
                      color="primary"
                      fontSize="small"
                    />
                  </IconButton>
                </Box>
                <Typography variant="caption">
                  Override parameters passed to the action&apos;s container at
                  runtime.
                </Typography>
              </Box>
              <Collapse in={openContainerParams} timeout="auto" unmountOnExit>
                <ActionContainerParams
                  workdir={workdir}
                  setWorkdir={setWorkdir}
                  command={command}
                  setCommand={setCommand}
                  entrypoint={entrypoint}
                  setEntrypoint={setEntrypoint}
                  envVars={envVars || {}}
                  setEnvVars={setEnvVars}
                />
              </Collapse>
            </Box>

            <Box>
              <Box sx={{ mb: theme.spacing(2) }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="subtitle2">
                    Compute Requirement Overrides (optional)
                  </Typography>
                  <IconButton
                    aria-label="open-compute-params"
                    size="small"
                    sx={{
                      ml: theme.spacing(0.25),
                      transform: openResourceParams ? "rotate(180deg)" : "none",
                    }}
                    onClick={() => setOpenResourceParams(!openResourceParams)}
                  >
                    <ExpandCircleDownOutlinedIcon
                      color="primary"
                      fontSize="small"
                    />
                  </IconButton>
                </Box>
                <Typography variant="caption">
                  Override compute resources required to run the action.
                </Typography>
              </Box>
              <Collapse in={openResourceParams} timeout="auto" unmountOnExit>
                <ActionComputeReqs
                  cpu={cpu}
                  setCpu={setCpu}
                  memory={memory}
                  setMemory={setMemory}
                  gpu={gpu}
                  setGpu={setGpu}
                  storage={storage}
                  setStorage={setStorage}
                  setHasComputeFormErrors={setHasComputeFormErrors}
                />
              </Collapse>
            </Box>

            {triggerType === TriggerType.Scheduled && (
              <Box>
                <Box sx={{ mb: theme.spacing(2) }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="subtitle2">
                      Output Dataset (optional)
                    </Typography>
                    <IconButton
                      aria-label="open-output-dataset"
                      size="small"
                      sx={{
                        ml: theme.spacing(0.25),
                        transform: openOutputDataset
                          ? "rotate(180deg)"
                          : "none",
                      }}
                      onClick={() => setOpenOutputDataset(!openOutputDataset)}
                    >
                      <ExpandCircleDownOutlinedIcon
                        color="primary"
                        fontSize="small"
                      />
                    </IconButton>
                  </Box>
                  <Typography variant="caption">
                    A dataset where the outputs of all scheduled invocations
                    will be uploaded.
                  </Typography>
                </Box>
                <Collapse in={openOutputDataset} timeout="auto" unmountOnExit>
                  <DatasetSelector
                    datasetId={outputDatasetId}
                    setDatasetId={setOutputDatasetId}
                    currentOrg={currentOrganization}
                  />
                </Collapse>
              </Box>
            )}
          </Stack>

          <Box sx={{ mt: theme.spacing(3) }}>
            <Box
              sx={{
                mt: theme.spacing(1),
                display: "flex",
                alignItems: "center",
              }}
            >
              <RobotoButton
                eventName={"CreateTriggerFormSubmitted"}
                eventProperties={{ name: name ?? "" }}
                variant={"contained"}
                color="secondary"
                disabled={triggerFormHasErrors({
                  name,
                  triggerType,
                  action,
                  actionTimeout,
                  requiredInputs,
                  hasComputeRequirementsErrors: hasComputeFormErrors,
                  hasScheduleErrors: scheduleError !== undefined,
                })}
                onClick={() => void createTrigger()}
              >
                Create Trigger
              </RobotoButton>
              {loading && (
                <CircularProgress sx={{ ml: theme.spacing(1.5) }} size="1rem" />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </PageLayout>
  );
}
